import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { collection, query, where, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { db } from '@shared/services/firebase';
import { applyRulesToTransactions } from '@shared/services/transactionsService';
import { subscribeToRecurringTransactions } from '@shared/services/recurringTransactionsService';
import { getPrimaryFromDetailed, getPlaidCategoryIconUrl } from '@shared/constants';
import { Transaction, RecurringTransaction } from '@shared/types';
import { cleanRawName } from '../../utils/formatUtils';
import { useLiveDataUid } from './useLiveDataUid';
import { useTransactionRulesContext } from './TransactionRulesProvider';
import { logError, logWarn } from '@/lib/log';

/**
 * Focused slice behind the DataContext facade: Plaid transactions (with
 * display-time rule/override application — no Firestore writes) and recurring
 * transactions. Also owns loadingData, which tracks the first transactions
 * snapshot — the historical "everything loaded" signal for the whole app.
 *
 * Must be mounted inside TransactionRulesProvider (rules feed the display-time
 * useMemo below).
 *
 * NOTE: components should keep consuming useData() from DataContext for now —
 * demo mode (DemoProviders) only stubs the facade's DataContext.Provider, so a
 * component reading this context directly would crash under /demo.
 */
interface TransactionsContextType {
    transactions: Transaction[];
    recurringTransactions: RecurringTransaction[];
    loadingData: boolean;
}

export const TransactionsContext = createContext<TransactionsContextType | undefined>(undefined);

export const useTransactionsContext = () => {
    const context = useContext(TransactionsContext);
    if (!context) {
        throw new Error('useTransactionsContext must be used within a TransactionsProvider');
    }
    return context;
};

// Helper to parse various date formats (returns UTC ISO string)
const parseDate = (d: any): string | undefined => {
    if (!d) return undefined;

    if (typeof d === 'string') {
        // If it's already an ISO string, return it
        if (d.includes('T') && d.includes('Z')) return d;
        // Otherwise, parse and convert to UTC ISO string
        return new Date(d).toISOString();
    }
    if (typeof d === 'number') return new Date(d).toISOString();

    // Handle object formats
    if (typeof d === 'object') {
        if ('__time__' in d) return d.__time__; // Spinwheel specific format
        if (typeof d.toDate === 'function') return d.toDate().toISOString(); // Firestore Timestamp
        if ('seconds' in d) return new Date(d.seconds * 1000).toISOString(); // Raw Firestore Timestamp
    }

    return undefined;
};

export const TransactionsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const uid = useLiveDataUid();
    const { transactionRules } = useTransactionRulesContext();
    const [rawTransactions, setRawTransactions] = useState<Transaction[]>([]);
    const [recurringTransactions, setRecurringTransactions] = useState<RecurringTransaction[]>([]);
    const [loadingData, setLoadingData] = useState(true);

    // Apply per-transaction category overrides first, then merchant-wide rules.
    // Per-tx overrides take priority over rules (rules won't re-override category).
    const transactions = useMemo(() => {
        const withOverrides = rawTransactions.map(tx => {
            if (!tx.userCategoryOverride) return tx;
            const newPrimary = getPrimaryFromDetailed(tx.userCategoryOverride);
            return {
                ...tx,
                categoryDetailed: tx.userCategoryOverride,
                category: newPrimary,
                // Replace stale Plaid merchant logo with the new category's icon
                logoUrl: getPlaidCategoryIconUrl(newPrimary),
            };
        });
        return applyRulesToTransactions(withOverrides, transactionRules);
    }, [rawTransactions, transactionRules]);

    useEffect(() => {
        if (!uid) {
            setRawTransactions([]);
            setRecurringTransactions([]);
            setLoadingData(false);
            return;
        }

        setLoadingData(true);
        const unsubscribes: Unsubscribe[] = [];

        // Fetch transactions from plaidTransactions collection
        // Document ID format: {uid}_{transaction_id}
        const transactionsQuery = query(
            collection(db, 'plaidTransactions'),
            where('uid', '==', uid)
        );

        unsubscribes.push(onSnapshot(transactionsQuery, (snapshot) => {
            const fetchedTransactions: Transaction[] = snapshot.docs
                .filter(doc => {
                    const data = doc.data();
                    // Filter out pending and removed transactions
                    return !data.pending && !data.removed;
                })
                .map(doc => {
                    const data = doc.data();

                    // Parse date (ensure UTC)
                    const dateStr = parseDate(data.date);
                    if (!dateStr) {
                        logWarn("Transaction missing date:", doc.id);
                    }

                    const amountInDollars = data.amount ? Number(data.amount) : 0;

                    // Get merchant name (prefer merchant_name, fallback to cleaned name)
                    // When merchant_name is null, the raw Plaid `name` often has reference numbers
                    // and codes (e.g. "ACH Electronic CreditGUSTO PAY 123456") — clean those out.
                    const merchant = data.merchant_name
                        || (data.name ? cleanRawName(data.name, 30) : 'Unknown Merchant');

                    // Get category (prefer PFM, then legacy array)
                    const pfc = data.personal_finance_category;
                    const category = Array.isArray(data.category) && data.category.length > 0
                        ? data.category[0]
                        : (typeof pfc === 'object' && pfc?.primary) || 'Other';
                    // PFM detailed category for goals/filtering (e.g. FOOD_AND_DRINK_FAST_FOOD)
                    let categoryDetailed: string | undefined;
                    if (typeof pfc === 'object' && (pfc?.detailed || pfc?.primary)) {
                        categoryDetailed = pfc.detailed || pfc.primary;
                    } else if (Array.isArray(data.category) && data.category.length > 0) {
                        // Legacy Plaid: ["Food and Drink", "Fast Food"] -> FOOD_AND_DRINK_FAST_FOOD
                        let legacy = data.category
                            .map((s: string) => String(s).toUpperCase().replace(/\s+/g, '_'))
                            .join('_');
                        // Normalize common Plaid variations to PFM format
                        legacy = legacy.replace(/RESTAURANTS$/i, 'RESTAURANT');
                        legacy = legacy.replace(/COFFEE_SHOP$/i, 'COFFEE');
                        categoryDetailed = legacy;
                    }

                    // Get logo URL: merchant logo → Plaid's category icon → our category icon URL
                    const logoUrl = data.logo_url
                        || data.personal_finance_category_icon_url
                        || getPlaidCategoryIconUrl(category);

                    // User reflection (needed/wanted/skip/ignored) — persisted on doc, survives Plaid sync (merge=True)
                    const reflection = data.reflection === 'needed' || data.reflection === 'wanted' || data.reflection === 'regret' || data.reflection === 'ignored'
                        ? data.reflection
                        : null;

                    // Keep the raw Plaid `name` when it differs from the clean merchant_name
                    const rawName = data.merchant_name && data.name && data.name !== data.merchant_name
                        ? data.name
                        : undefined;

                    return {
                        id: doc.id, // Use full document ID as transaction ID
                        merchant: merchant,
                        rawName,
                        amount: amountInDollars,
                        date: dateStr || new Date().toISOString(), // Fallback to current date if missing
                        category: category,
                        categoryDetailed,
                        logoUrl: logoUrl,
                        website: data.website || undefined,
                        merchantEntityId: data.merchant_entity_id || undefined,
                        counterparties: data.counterparties || undefined,
                        paymentChannel: data.payment_channel || undefined,
                        reflection,
                        reflectionSource: data.reflectionSource || null,
                        reflectionUpdatedAt: data.reflectionUpdatedAt || null,
                        userNote: data.userNote || undefined,
                        userCategoryOverride: data.userCategoryOverride || undefined,
                        accountId: data.account_id || undefined,
                    };
                });

            // Sort by date descending (most recent first)
            fetchedTransactions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

            setRawTransactions(fetchedTransactions);

            // Historically the transactions query was the app's slowest listener,
            // so its first snapshot is the "data loaded" signal.
            setLoadingData(false);
        }, (error) => {
            logError(error, { context: 'fetch_transactions' });
            setLoadingData(false);
        }));

        // Subscribe to recurring transactions (Plaid subscriptions)
        unsubscribes.push(subscribeToRecurringTransactions(uid, (txns) => {
            setRecurringTransactions(txns);
        }));

        return () => {
            unsubscribes.forEach(unsubscribe => unsubscribe());
        };
    }, [uid]);

    const value = useMemo(() => ({
        transactions,
        recurringTransactions,
        loadingData,
    }), [transactions, recurringTransactions, loadingData]);

    return (
        <TransactionsContext.Provider value={value}>
            {children}
        </TransactionsContext.Provider>
    );
};
